import dayjs from "dayjs";
import { downloadCsv } from "./adminTableCsv";

export type CompanyExportRow = {
  id: number;
  created_at: string;
  name: string;
  status: string | null;
  country: string | null;
  city: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  vat_number: string | null;
  eori_number: string | null;
  coc_number: number | null;
  phone_number: string | null;
  invoice_email?: string | null;
  article_23: boolean | null;
  payment_arrears?: boolean;
  pricing_payment_term?: number | { id: number; days?: number } | null;
  wms_provider?: "picqer" | "channeldock" | null;
  picqer_enabled?: boolean;
  channeldock_enabled?: boolean;
  picqer_manual_orders_enabled?: boolean;
  moneybird_contact_id?: string | null;
  leancustoms_organization_id?: string | null;
  api_access_granted?: boolean;
};

export type CompanyExportInvoiceStats = {
  invoiceCount: number;
  totalRevenue: number;
  outstandingBalance: number;
};

const HEADERS = [
  "ID",
  "Name",
  "Status",
  "Country",
  "City",
  "Street",
  "House Number",
  "Postal Code",
  "VAT Number",
  "EORI Number",
  "COC Number",
  "Phone",
  "Invoice Email",
  "Article 23",
  "Payment Arrears",
  "Payment Term Days",
  "Active WMS",
  "Picqer Credentials",
  "ChannelDock Credentials",
  "WMS Manual Orders",
  "Moneybird Contact ID",
  "LeanCustoms Org ID",
  "API Access Granted",
  "Created At",
  "Total Invoices",
  "Total Revenue",
  "Outstanding Balance",
];

function cell(value: string | number | boolean | null | undefined): string {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function paymentTermDays(
  term: CompanyExportRow["pricing_payment_term"]
): string {
  if (term == null) return "";
  if (typeof term === "object") {
    return term.days != null ? String(term.days) : String(term.id);
  }
  return String(term);
}

function companyToRow(
  company: CompanyExportRow,
  stats: CompanyExportInvoiceStats | undefined
): string[] {
  return [
    cell(company.id),
    cell(company.name),
    cell(company.status),
    cell(company.country),
    cell(company.city),
    cell(company.street),
    cell(company.house_number),
    cell(company.postal_code),
    cell(company.vat_number),
    cell(company.eori_number),
    cell(company.coc_number),
    cell(company.phone_number),
    cell(company.invoice_email),
    cell(company.article_23),
    cell(company.payment_arrears ?? false),
    paymentTermDays(company.pricing_payment_term),
    cell(company.wms_provider ?? ""),
    cell(!!(company.picqer_enabled)),
    cell(!!company.channeldock_enabled),
    cell(!!company.picqer_manual_orders_enabled),
    cell(company.moneybird_contact_id),
    cell(company.leancustoms_organization_id),
    cell(!!company.api_access_granted),
    company.created_at
      ? dayjs(company.created_at).format("YYYY-MM-DD HH:mm:ss")
      : "",
    cell(stats?.invoiceCount ?? 0),
    stats ? stats.totalRevenue.toFixed(2) : "0.00",
    stats ? stats.outstandingBalance.toFixed(2) : "0.00",
  ];
}

export function downloadAdminCompaniesCsv(
  companies: CompanyExportRow[],
  invoiceStats: Record<number, CompanyExportInvoiceStats> = {},
  filename = `companies_export_${dayjs().format("YYYY-MM-DD")}.csv`
): void {
  downloadCsv(
    HEADERS,
    companies.map((company) => companyToRow(company, invoiceStats[company.id])),
    filename
  );
}
